/**
 * Close-project suggestion banner (Patch E18).
 *
 * Mirrors `ndc-suggestion-banner.tsx`. Renders when `shouldSuggestClose`
 * fires — i.e. NDC is issued, every part has reached stage 18, and the
 * project is still open. Action button scrolls to / focuses the existing
 * Close Project button in the page header rather than opening a modal of
 * its own, since the existing close-project flow already has the
 * confirmation modal it needs.
 *
 * Dismissal is per-session, matching the NDC banner — closing the project
 * is a real-world action and the user shouldn't be able to hide the
 * reminder permanently by accident.
 */
import { useState } from 'react';
import { CheckCheck, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { shouldSuggestClose } from '@/lib/close-suggestion';
import { useProjectItems } from '@/lib/pipeline-queries';
import type { Project } from '@/lib/project-queries';

interface Props {
  project: Project;
}

export function CloseProjectSuggestionBanner({ project }: Props) {
  const items = useProjectItems(project.id);
  const [dismissed, setDismissed] = useState(false);

  if (dismissed) return null;
  if (!items.data) return null;
  if (!shouldSuggestClose(project, items.data)) return null;

  const scrollToCloseButton = (): void => {
    // The header Close Project button is rendered via the PageHeader's right
    // slot; we identify it by data attribute so the banner doesn't have to
    // own a reference to it.
    const btn = document.querySelector<HTMLButtonElement>(
      'button[data-close-project-button="true"]',
    );
    if (btn) {
      btn.scrollIntoView({ behavior: 'smooth', block: 'center' });
      btn.focus();
    }
  };

  return (
    <div
      role="status"
      className="mb-4 flex items-start gap-3 rounded-md border border-success-border bg-success-subtle px-4 py-3"
    >
      <CheckCheck
        className="mt-0.5 h-4 w-4 shrink-0 text-success-fg"
        strokeWidth={1.5}
      />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-success-fg">
          Billing complete and NDC issued.
        </p>
        <p className="mt-0.5 text-xs text-secondary">
          Close the project to mark it complete and freeze its history.
        </p>
      </div>
      <Button variant="primary" onClick={scrollToCloseButton}>
        Close project
      </Button>
      <button
        type="button"
        onClick={() => setDismissed(true)}
        aria-label="Dismiss"
        className="rounded-md p-1 text-secondary hover:bg-success-subtle hover:text-primary"
      >
        <X className="h-4 w-4" strokeWidth={1.5} />
      </button>
    </div>
  );
}
